import Store from '~/store/Store';
import { logoutUser } from '~/ducks/authentication/actions';
import type { AppState } from '~/reducers';
import fetchHelper from '~/utils/fetch-helper/fetch-helper';
import logger from '~/utils/logger/logger';
import { mutex } from '~/domain/auth/utils/auth-lock';
import { replayWhileUnauthorized } from '~/domain/auth/utils/refresh-replay';
import {
  refreshToken,
  shouldUseNativeRefreshToken,
} from '~/domain/native/utils/refreshTokenNative';

type RefreshResult = { status: number | undefined; refreshed?: boolean };

const replayRequest = (req: Request) =>
  replayWhileUnauthorized(
    () => fetch(req.clone()),
    (response) => response.status === 401
  );

const requestRefresh = async (): Promise<RefreshResult> => {
  if (shouldUseNativeRefreshToken()) {
    return { status: await refreshToken() };
  }
  const response = await fetch(
    fetchHelper.createPostRequest(`${process.env.ENDPOINT_BFF}/token/refresh`)
  );
  const body = await response.json().catch(() => undefined);
  return { status: response.status, refreshed: body?.refreshed };
};

const isSessionEnded = () => (Store.getState() as AppState).authentication?.authenticated === false;

const logoutOnAuthFailure = (refreshStatus: number | undefined) => {
  if (refreshStatus !== 401) {
    return;
  }
  if ((Store.getState() as AppState).authentication.authenticated) {
    Store.dispatch(logoutUser());
  }
};

const refreshAndReplay = async (req: Request, res: Response) => {
  logger.debug('fetch-wrapper:handle-unauthenticated-error', 'Acquiring lock');
  const release = await mutex.acquire();
  try {
    const refreshResult = await requestRefresh();
    logger.debug('fetch-wrapper:handle-unauthenticated-error', refreshResult);

    if (refreshResult.status === 200) {
      logger.log('fetch-wrapper:handle-unauthenticated-error', 'Token refreshed');
      return refreshResult.refreshed === false ? await replayRequest(req) : await fetch(req);
    }

    logger.log(
      'fetch-wrapper:handle-unauthenticated-error',
      'Token refresh failed',
      refreshResult.status
    );
    logoutOnAuthFailure(refreshResult.status);
    return res;
  } finally {
    release();
  }
};

const handleUnauthenticatedError = async (res: Response, req: Request): Promise<Response> => {
  if (res.status !== 401) {
    return res;
  }
  logger.log('fetch-wrapper:handle-unauthenticated-error', 'Received 401 response');

  if (mutex.isLocked()) {
    await mutex.waitForUnlock();
    return isSessionEnded() ? res : await replayRequest(req);
  }
  return await refreshAndReplay(req, res);
};

export default handleUnauthenticatedError;
